import { create } from 'zustand';
import type { CelebrationData } from '../components/CelebrationOverlay';

interface CelebrationState {
  currentCelebration: CelebrationData | null;
  queue: CelebrationData[];
  celebrate: (data: CelebrationData) => void;
  completeCelebration: () => void;
}

export const useCelebrationStore = create<CelebrationState>((set, get) => ({
  currentCelebration: null,
  queue: [],

  celebrate: (data: CelebrationData) => {
    const { currentCelebration, queue } = get();

    if (!currentCelebration) {
      // No celebration active, show immediately
      set({ currentCelebration: data });
    } else {
      // Queue it for later
      set({ queue: [...queue, data] });
    }
  },

  completeCelebration: () => {
    const { queue } = get();

    if (queue.length > 0) {
      // Show next in queue
      const [next, ...rest] = queue;
      set({ currentCelebration: next, queue: rest });
    } else {
      // No more celebrations
      set({ currentCelebration: null });
    }
  },
}));

/**
 * Hook to trigger celebrations throughout the app
 *
 * @example
 * const { celebrate } = useCelebration();
 *
 * // On lesson complete
 * celebrate({
 *   type: 'lesson_complete',
 *   message: '¡Lección completada!',
 *   subMessage: 'Has completado 5 lecciones esta semana',
 *   xpEarned: 25,
 * });
 */
export function useCelebration() {
  const celebrate = useCelebrationStore((state) => state.celebrate);

  return {
    celebrate,

    // Convenience methods for common celebrations
    celebrateLessonComplete: (lessonTitle: string, xpEarned?: number) => {
      celebrate({
        type: 'lesson_complete',
        message: '¡Lección completada!',
        subMessage: lessonTitle,
        xpEarned,
      });
    },

    celebrateStreakMilestone: (days: number, xpEarned?: number) => {
      celebrate({
        type: 'streak_milestone',
        message: `¡${days} días de racha!`,
        subMessage: '¡Increíble constancia! Sigue así.',
        xpEarned,
      });
    },

    celebrateQuizPassed: (score: number, xpEarned?: number) => {
      celebrate({
        type: 'quiz_passed',
        message: '¡Quiz aprobado!',
        subMessage: `Obtuviste ${score}% de respuestas correctas`,
        xpEarned,
      });
    },

    celebrateLevelUp: (newLevel: string, xpEarned?: number) => {
      celebrate({
        type: 'level_up',
        message: '¡Subiste de nivel!',
        subMessage: `Ahora estás en ${newLevel}`,
        xpEarned,
      });
    },

    celebrateAchievement: (achievementName: string, xpEarned?: number) => {
      celebrate({
        type: 'achievement',
        message: '¡Logro desbloqueado!',
        subMessage: achievementName,
        xpEarned,
      });
    },

    celebrateFirstLessonToday: (xpEarned?: number) => {
      celebrate({
        type: 'first_lesson_today',
        message: '¡Primera lección del día!',
        subMessage: 'Buen comienzo, sigue aprendiendo',
        xpEarned,
      });
    },
  };
}
